import { useCallback, useEffect, useState } from 'react';

import { getProxyDelegators, getProxyDirectoryStats } from 'api/proxy-projection';

import { isContractDelegationEnabled } from 'constants/ice-delegation';

import { getEnv } from 'helpers/env';

import { StellarService } from 'services/globalServices';
import { StellarEvents } from 'services/stellar/events/events';

import type { ProxyProjectionState } from 'types/proxy-projection';

// The projection API serves its database, which follows the chain on its own schedule.
const REFRESH_INTERVAL_MS = 30_000;

const useProjection = <T>(
    identity: string,
    enabled: boolean,
    load: () => Promise<T>,
): ProxyProjectionState<T> => {
    const key = `${getEnv()}:${identity}:${enabled}`;
    const [result, setResult] = useState<{ key: string; state: ProxyProjectionState<T> } | null>(
        null,
    );
    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        let busy = false;
        const refresh = async () => {
            if (busy) return;
            busy = true;
            try {
                const data = await load();
                if (!cancelled) setResult({ key, state: { status: 'ready', data } });
            } catch {
                if (!cancelled) setResult({ key, state: { status: 'unavailable' } });
            } finally {
                busy = false;
            }
        };
        void refresh();
        const interval = window.setInterval(refresh, REFRESH_INTERVAL_MS);
        const unsubscribe = StellarService.event.sub(({ type }) => {
            if (type === StellarEvents.contractDelegationUpdate) void refresh();
        });
        return () => {
            cancelled = true;
            window.clearInterval(interval);
            unsubscribe();
        };
    }, [key, enabled, load]);
    if (!enabled) return { status: 'disabled' };
    return result?.key === key ? result.state : { status: 'loading' };
};

export const useProxyDelegators = (owner: string) => {
    const load = useCallback(() => getProxyDelegators(owner), [owner]);
    return useProjection(owner, isContractDelegationEnabled() && Boolean(owner), load);
};

export const useProxyDirectoryStats = (accounts: string[]) => {
    const identity = JSON.stringify([...new Set(accounts)].sort());
    const load = useCallback(() => getProxyDirectoryStats(JSON.parse(identity)), [identity]);
    return useProjection(identity, isContractDelegationEnabled(), load);
};
